import React, { useEffect, useState, useContext, useMemo } from 'react'
import { Container } from '../../../components'
import { IdBoolMap, Inspection, Unit, User, Folder } from '../../../models'
import {
    useAppDispatch,
    useAptConfig,
    _useFinderSelection,
    useRootInfrastructure,
} from '../../../hooks'
import { SchedulerFinder } from '../../Scheduler_v2/SchedulerFinder'
import {
    FinderLocationSelection,
    FinderSelectionMode,
    RootState,
    setOfflineUnitsInspection,
} from '../../../store'
import {
    Button,
    Paper,
    Theme,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Chip,
} from '@material-ui/core'
import { useSelector } from 'react-redux'
import { AssignmentProgressContext } from './CreateInspectionModal'
import InfoIcon from '@material-ui/icons/Info'

interface Props {
    theme: Theme
    inspection: Inspection
    assignedUnits: Unit[]
    onClose: () => void
}

export const SelectOfflineUnits = (props: Props) => {
    const { theme, inspection, assignedUnits, onClose } = props
    const { progress } = useContext(AssignmentProgressContext)

    const [selectedUnits, setSelectedUnits] = useState<IdBoolMap>({})
    const [offlineUnits, setOfflineUnits] = useState<IdBoolMap>({})
    const [totalSelectedCount, setTotalSelectedCount] = useState<number>(0)
    const [offlineSelectedCount, setOfflineSelectedCount] = useState<number>(0)
    const [isTreeFiltered, setIsTreeFiltered] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const loadingState = useSelector(
        (state: RootState) => state.inspection.isLoading,
    )

    const dispatch = useAppDispatch()

    const { getAreaConfigMap } = useAptConfig({ areaConfigList: true })

    const { tree } = useRootInfrastructure(true)

    // Filter the root folder structure to only include units from assignedUnits
    const filterRootForAssignedUnits = () => {
        setIsLoading(true)

        if (!tree?.root || !assignedUnits || assignedUnits.length === 0) {
            setIsLoading(false)
            return tree?.root
        }

        // Create a set of assigned unit IDs for faster lookup
        const assignedUnitIds = new Set(assignedUnits.map((unit) => unit.id))

        // Create a clean copy without circular references
        const createCleanFolder = (folder: Folder): Folder => {
            // Create a new object with only the properties we need
            const cleanFolder: Folder = {
                id: folder.id,
                name: folder.name,
                path: folder.path,
                pathIds: folder.pathIds,
                parent: null, // Break the circular reference
                apartmentOwner: folder.apartmentOwner,
                units: [],
                children: [],
                lineageUnitCount: folder.lineageUnitCount,
            }

            // Filter and copy units
            cleanFolder.units = folder.units
                .filter((unit: Unit) => assignedUnitIds.has(unit.id))
                .map((unit: Unit) => ({
                    ...unit,
                    folder: { id: folder.id } as Folder, // Just keep the folder ID reference
                }))

            // Filter and copy children recursively
            cleanFolder.children = folder.children
                .map((child: Folder) => createCleanFolder(child))
                .filter(
                    (child: Folder) =>
                        child.units.length > 0 || child.children.length > 0,
                )

            return cleanFolder
        }

        // Create a clean copy of the root
        const filteredRoot = createCleanFolder(tree.root)
        console.log('Filtered root:', filteredRoot)

        setIsTreeFiltered(true)
        setIsLoading(false)
        return filteredRoot
    }

    // Get the filtered root structure
    const filteredRoot = useMemo(() => {
        if (tree?.root && assignedUnits?.length > 0) {
            return filterRootForAssignedUnits()
        }
        return tree?.root
    }, [tree?.root, assignedUnits?.length])
    console.log('Filtered root (from useMemo):', filteredRoot)

    const finderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.RootSelection,
        selectionMode: FinderSelectionMode.Recursive,
    })

    const areaConfigMap = getAreaConfigMap()

    // Initialize counters with the total assigned units
    useEffect(() => {
        if (assignedUnits && assignedUnits.length > 0) {
            // Set the total count to the number of assigned units
            setTotalSelectedCount(assignedUnits.length)

            // Initialize with all units online, none offline
            setOfflineSelectedCount(0)

            console.log(
                `Initialized with ${assignedUnits.length} online units, 0 offline units`,
            )
        }
    }, [assignedUnits])

    // Handle selection from SchedulerFinder
    useEffect(() => {
        // Listen to changes in finderSelection and update offline counts
        if (finderSelection?.selection) {
            const unitSelections = finderSelection.selection.unit
            console.log('Unit selections updated:', unitSelections)

            // Count selected units from LocationSelection (these will be offline)
            let selectedCount = 0
            const selectedIds = []

            for (const unitId in unitSelections) {
                // Skip the 'length' property
                if (unitId === 'length') continue

                // Only count defined/non-null unit selections
                if (unitSelections[(unitId as unknown) as number]) {
                    selectedCount++
                    selectedIds.push(Number(unitId))
                }
            }

            console.log('Selected unit IDs for offline:', selectedIds)
            setOfflineSelectedCount(selectedCount)

            // Update selected units map - these will be offline
            const newSelectedUnits: IdBoolMap = {}
            const newOfflineUnits: IdBoolMap = {} // Start fresh, don't maintain previous

            for (const unitId in unitSelections) {
                if (unitId === 'length') continue

                if (unitSelections[(unitId as unknown) as number]) {
                    const numId = Number(unitId)
                    if (!isNaN(numId)) {
                        newSelectedUnits[numId] = true
                        // Selected units are offline
                        newOfflineUnits[numId] = true
                    }
                }
            }

            console.log('New selected offline units map:', newSelectedUnits)
            setSelectedUnits(newSelectedUnits)
            setOfflineUnits(newOfflineUnits)
        }
    }, [finderSelection?.selection])

    // Determine if progress is complete and animation should be minimized
    const isProgressComplete = progress.status === 'completed'

    const handleCompleteSetup = () => {
        const offlineUnits = assignedUnits.filter(
            (unit) => selectedUnits[unit.id],
        )
        if (offlineUnits.length > 0) {
            // Just pass the unit IDs to avoid circular reference issues
            const unitIds = offlineUnits.map((unit) => unit.id)
            console.log('Unit IDs:', unitIds)

            dispatch(
                setOfflineUnitsInspection({
                    inspection: inspection.id,
                    units: unitIds,
                }),
            )
        }
        onClose()
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                padding: theme.spacing(2),
                paddingBottom: theme.spacing(12),
                height: '100%',
                position: 'relative',
            }}
        >
            {/* Main Content - Two Column Layout */}
            <Grid
                container
                spacing={3}
                style={{
                    flexGrow: 1,
                    marginBottom: theme.spacing(8), // Add spacing before the bottom bar
                }}
            >
                {/* Left Column - Unit Selection Tree (60%) */}
                <Grid item xs={5}>
                    <Paper
                        style={{
                            height: '100%',
                            padding: theme.spacing(2),
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            Select Offline Units (Optional)
                            <InfoIcon
                                style={{
                                    fontSize: 18,
                                    marginLeft: theme.spacing(1),
                                    color: theme.palette.info.main,
                                    verticalAlign: 'middle',
                                    cursor: 'help',
                                }}
                                titleAccess="Select units that will be inspected offline. By default, all assigned units are set for online inspection. If you want to inspect all units online, simply click the Complete Setup button below."
                            />
                        </Typography>

                        <div
                            style={{
                                height: '400px',
                                overflowY: 'auto',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: theme.shape.borderRadius,
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <SchedulerFinder
                                root={filteredRoot || tree.root}
                                finderSelection={finderSelection}
                                areaConfigMap={areaConfigMap}
                                areaStatusTagMap={{}}
                                unitWorkorderMap={{}}
                            />
                        </div>
                    </Paper>
                </Grid>

                {/* Right Column - Info about Offline Inspections (40%) */}
                <Grid item xs={7}>
                    <Card
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Typography variant="h6" component="h2">
                                    About Offline Video Inspections
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                paragraph
                            >
                                Offline inspections allow you to complete
                                inspections without an internet connection. Data
                                will be synced automatically when you reconnect.
                                This is perfect for areas with poor connectivity
                                or when you need to work in remote locations.
                            </Typography>

                            {/* Video Thumbnail
                            <Box
                                position="relative"
                                mb={2}
                                style={{
                                    backgroundColor: theme.palette.grey[200],
                                    height: 180,
                                    borderRadius: theme.shape.borderRadius,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Box
                                        style={{
                                            width: 60,
                                            height: 60,
                                            backgroundColor:
                                                'rgba(255,255,255,0.8)',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <PlayArrowIcon
                                            style={{
                                                fontSize: 40,
                                                color:
                                                    theme.palette.primary.main,
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Typography
                                    variant="caption"
                                    style={{
                                        position: 'absolute',
                                        bottom: 10,
                                        right: 10,
                                        color: 'white',
                                        background: 'rgba(0,0,0,0.5)',
                                        padding: '2px 6px',
                                        borderRadius: 4,
                                    }}
                                >
                                    2:30
                                </Typography>
                            </Box> */}
                            {/* <Typography
                                variant="subtitle2"
                                align="center"
                                style={{ marginBottom: theme.spacing(2) }}
                            >
                                Watch tutorial: How to perform offline
                                inspections
                            </Typography> */}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Spacer to ensure content doesn't get hidden behind the bottom bar */}
            <div style={{ height: theme.spacing(8) }}></div>

            {/* Bottom Bar - Fixed to bottom with counters and action button */}
            <Paper
                style={{
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: theme.spacing(2),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: theme.spacing(2),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 10,
                }}
                elevation={3}
            >
                {/* Unit Selection Counters */}
                <Box>
                    <Typography
                        variant="body2"
                        component="span"
                        style={{ fontWeight: 500 }}
                    >
                        Units:
                    </Typography>
                    <Chip
                        label={`${
                            (assignedUnits?.length || 0) - offlineSelectedCount
                        } online`}
                        size="small"
                        style={{
                            marginLeft: theme.spacing(1),
                            backgroundColor: theme.palette.primary.main,
                            color: '#fff',
                            fontWeight: 500,
                        }}
                    />
                    <Chip
                        label={`${offlineSelectedCount} offline`}
                        size="small"
                        style={{
                            marginLeft: theme.spacing(1),
                            backgroundColor:
                                offlineSelectedCount > 0
                                    ? theme.palette.secondary.main
                                    : theme.palette.grey[300],
                            color:
                                offlineSelectedCount > 0
                                    ? '#fff'
                                    : theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    />
                </Box>

                {/* Action Buttons */}
                <Box>
                    <Button
                        onClick={handleCompleteSetup}
                        variant="contained"
                        color="primary"
                        disabled={
                            progress.status === 'pending' ||
                            progress.status === 'in_progress'
                        }
                    >
                        {offlineSelectedCount > 0
                            ? `Complete Setup with ${offlineSelectedCount} Offline Units`
                            : `Complete Setup (All Online)`}
                    </Button>
                </Box>
            </Paper>
        </Container>
    )
}
